import React, { useState, useEffect } from 'react';
import timeLogService, { TimeLog, TimeLogStats } from '../services/timeLogService';
import TimeLogEditModal from '../components/TimeLogEditModal';
import './TimeLogCalendar.css';

const TimeLogCalendar: React.FC = () => {
  const [timeLogs, setTimeLogs] = useState<TimeLog[]>([]);
  const [stats, setStats] = useState<TimeLogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [editingLog, setEditingLog] = useState<TimeLog | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchTimeLogs();
    fetchStats();
  }, [currentMonth]);

  const fetchTimeLogs = async () => {
    try {
      console.log('🔄 Fetching time logs...');
      setLoading(true);
      const response = await timeLogService.getAll();
      console.log('✅ Time logs received:', response);
      setTimeLogs(response.data);
      setError(null);
    } catch (err: any) {
      console.error('❌ Error fetching time logs:', err);
      setError(err.response?.data?.message || 'Failed to fetch time logs');
      console.error('Error fetching time logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await timeLogService.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleDayClick = (date: Date, existingLog?: TimeLog) => {
    if (existingLog) {
      setEditingLog(existingLog);
    } else {
      // Create new log for this date
      setEditingLog({
        date: date.toISOString(),
        title: '',
        hours: 0,
        tasks: [''],
        businessValue: [''],
        category: 'Development',
        status: 'completed'
      });
    }
    setShowModal(true);
  };

  const handleSave = async (timeLog: TimeLog) => {
    try {
      if (timeLog._id) {
        await timeLogService.update(timeLog._id, timeLog);
      } else {
        await timeLogService.create(timeLog);
      }
      setShowModal(false);
      setEditingLog(null);
      fetchTimeLogs();
      fetchStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save time log');
      console.error('Error saving time log:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this time log entry?')) {
      try {
        await timeLogService.delete(id);
        setShowModal(false);
        setEditingLog(null);
        fetchTimeLogs();
        fetchStats();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete time log');
        console.error('Error deleting time log:', err);
      }
    }
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="day-cell empty" />);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      date.setHours(0, 0, 0, 0);
      
      const dateStr = date.toISOString().split('T')[0];
      const logsForDay = (timeLogs || []).filter(log => {
        const logDate = new Date(log.date);
        logDate.setHours(0, 0, 0, 0);
        return logDate.toISOString().split('T')[0] === dateStr;
      });

      const totalHours = logsForDay.reduce((sum, log) => sum + log.hours, 0);
      const isToday = date.getTime() === today.getTime();
      const hasActivity = logsForDay.length > 0;
      const isPlanned = logsForDay.some(log => log.status === 'planned');

      let className = 'day-cell';
      if (hasActivity) className += ' has-activity';
      if (isToday) className += ' today';
      if (isPlanned && !logsForDay.some(log => log.status === 'completed')) className += ' planned';

      days.push(
        <div
          key={day}
          className={className}
          onClick={() => handleDayClick(date, logsForDay[0])}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleDayClick(date, logsForDay[0]);
            }
          }}
          tabIndex={0}
          role="button"
          aria-label={`${date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}${hasActivity ? `, ${totalHours} hours logged` : ', click to add entry'}`}
        >
          <div className="day-number">{day}</div>
          {hasActivity && (
            <>
              <div className="day-activity">{logsForDay[0].title}</div>
              {totalHours > 0 && (
                <span className="hours-badge" aria-label={`${totalHours} hours`}>
                  {totalHours}h
                </span>
              )}
            </>
          )}
        </div>
      );
    }

    return days;
  };

  const renderListView = () => {
    const sortedLogs = [...(timeLogs || [])].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    return (
      <div className="list-view">
        {sortedLogs.map(log => (
          <div
            key={log._id}
            className="activity-card"
            onClick={() => handleDayClick(new Date(log.date), log)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleDayClick(new Date(log.date), log);
              }
            }}
            tabIndex={0}
            role="button"
            aria-label={`${log.title}, ${log.hours} hours, ${new Date(log.date).toLocaleDateString()}, click to edit`}
          >
            <div className="activity-header">
              <div>
                <h3 className="activity-title">{log.title}</h3>
                <p className="activity-date">
                  {new Date(log.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <div className="activity-time" aria-label={`${log.hours} hours`}>
                {log.hours}h
              </div>
            </div>
            {log.tasks && log.tasks.length > 0 && (
              <div className="activity-tasks">
                <h4>Tasks Completed:</h4>
                <ul>
                  {log.tasks.map((task, idx) => (
                    <li key={idx}>{task}</li>
                  ))}
                </ul>
              </div>
            )}
            {log.businessValue && log.businessValue.length > 0 && (
              <div className="business-value">
                <h4>Business Value:</h4>
                <ul>
                  {log.businessValue.map((value, idx) => (
                    <li key={idx}>{value}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const changeMonth = (delta: number) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + delta);
      return newDate;
    });
  };

  if (loading && timeLogs.length === 0) {
    return (
      <div className="time-log-container">
        <div className="loading" role="status" aria-live="polite">
          Loading time logs...
        </div>
      </div>
    );
  }

  return (
    <div className="time-log-container">
      <header className="time-log-header">
        <h1>🚀 E-Unify Development Time Log</h1>
        <p className="subtitle">Personal Activity Tracking</p>
      </header>

      {error && (
        <div className="error-message" role="alert" aria-live="assertive">
          {error}
        </div>
      )}

      {stats && (
        <div className="summary-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.summary.totalHours}</div>
            <div className="stat-label">Total Hours</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.summary.totalEntries}</div>
            <div className="stat-label">Activities Logged</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.summary.completedEntries}</div>
            <div className="stat-label">Completed</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.summary.avgHoursPerEntry.toFixed(1)}</div>
            <div className="stat-label">Avg Hours/Entry</div>
          </div>
        </div>
      )}

      <div className="view-toggle">
        <button
          className={viewMode === 'calendar' ? 'active' : ''}
          onClick={() => setViewMode('calendar')}
          aria-pressed={viewMode === 'calendar'}
        >
          📅 Calendar View
        </button>
        <button
          className={viewMode === 'list' ? 'active' : ''}
          onClick={() => setViewMode('list')}
          aria-pressed={viewMode === 'list'}
        >
          📋 List View
        </button>
      </div>

      {viewMode === 'calendar' ? (
        <div className="calendar-section">
          <div className="month-header">
            <button
              onClick={() => changeMonth(-1)}
              className="month-nav-btn"
              aria-label="Previous month"
            >
              ‹
            </button>
            <h2 className="month-title">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h2>
            <button
              onClick={() => changeMonth(1)}
              className="month-nav-btn"
              aria-label="Next month"
            >
              ›
            </button>
          </div>
          <div className="calendar-grid">
            <div className="day-header">Sun</div>
            <div className="day-header">Mon</div>
            <div className="day-header">Tue</div>
            <div className="day-header">Wed</div>
            <div className="day-header">Thu</div>
            <div className="day-header">Fri</div>
            <div className="day-header">Sat</div>
            {renderCalendar()}
          </div>
        </div>
      ) : (
        renderListView()
      )}

      {showModal && editingLog && (
        <TimeLogEditModal
          timeLog={editingLog}
          onSave={handleSave}
          onDelete={editingLog._id ? handleDelete : undefined}
          onClose={() => {
            setShowModal(false);
            setEditingLog(null);
          }}
        />
      )}
    </div>
  );
};

export default TimeLogCalendar;
